import {
  DEFAULT_MODEL_BY_PROVIDER,
  type ModelCapabilities,
  type ProviderKind,
  type ServerProvider,
  type ServerProviderModel,
} from "@cmmd-forge/contracts";
import { isServerProviderExecutable } from "@cmmd-forge/shared/providerExecution";
import { normalizeModelSlug } from "@cmmd-forge/shared/model";
import type { ForgeProviderAccessState, ProviderCredentialMode } from "./cmmd/forgeProviderAccess";
import {
  resolveLockedProviderAvailability,
  resolveRunnableProviderSnapshots,
} from "./cmmd/providerAvailabilityJoin";

const EMPTY_CAPABILITIES: ModelCapabilities = {
  reasoningEffortLevels: [],
  supportsFastMode: false,
  supportsThinkingToggle: false,
  contextWindowOptions: [],
  promptInjectedEffortLevels: [],
};

const FALLBACK_PROVIDER_ORDER: readonly ProviderKind[] = ["codex", "claudeAgent", "apex"];

/**
 * The provider to start on, or null when the list names none.
 *
 * Null rather than Codex, since FOR-36439. This used to answer "codex" for a
 * list it had never seen, and the `/new` draft consulted it while the target
 * runtime's inventory was still unknown, so a Send aimed at no provider in
 * particular would have run the person's own Codex subscription. Absence of
 * data is not evidence for a provider, and every caller that must produce a
 * pair anyway now names its own fallback where a reader can see it.
 */
export function resolveDefaultSelectableProvider(
  providers: ReadonlyArray<ServerProvider>,
): ProviderKind | null {
  for (const provider of FALLBACK_PROVIDER_ORDER) {
    const candidate = providers.find((p) => p.provider === provider);
    if (candidate && isSelectableProviderSnapshot(candidate)) {
      return provider;
    }
  }
  return providers.find(isSelectableProviderSnapshot)?.provider ?? null;
}

export function getProviderModels(
  providers: ReadonlyArray<ServerProvider>,
  provider: ProviderKind,
): ReadonlyArray<ServerProviderModel> {
  return providers.find((candidate) => candidate.provider === provider)?.models ?? [];
}

function isSelectableProviderSnapshot(candidate: ServerProvider): boolean {
  return isServerProviderExecutable(candidate);
}

export function resolveSelectableProvider(
  providers: ReadonlyArray<ServerProvider>,
  provider: ProviderKind | null | undefined,
): ProviderKind {
  // The explicit Codex here is the same answer this function's last line gives
  // and is deliberate: this resolver's contract is that it always names a
  // provider, because it heals a stored selection for a live connection.
  const requested = provider ?? resolveDefaultSelectableProvider(providers) ?? "codex";
  const requestedSnapshot = providers.find((p) => p.provider === requested);
  if (requestedSnapshot && isSelectableProviderSnapshot(requestedSnapshot)) {
    return requested;
  }
  if (requested === "codex") {
    return requested;
  }

  for (const preferredProvider of FALLBACK_PROVIDER_ORDER) {
    const candidate = providers.find((p) => p.provider === preferredProvider);
    if (candidate && isSelectableProviderSnapshot(candidate)) {
      return preferredProvider;
    }
  }

  return "codex";
}

/**
 * The providers a display surface may offer: the Pick projection of the join's
 * one availability evaluation, and the display-side twin of the join's
 * `resolveRunnableProviderSnapshots`, which filters the same shape by the Run
 * verdict for the send path. The Pick projection deliberately discards the
 * transient `runtime-unavailable` answer the eligibility backend derives from
 * an absent or non-executable snapshot, so a provider the Environment publishes
 * stays offered through a readiness flap; durable readiness facts still
 * withhold it, with the reason the join rows render. See
 * docs/adr/pick-and-run-are-projections-of-one-availability-evaluation.md.
 */
export function resolvePickableProviderSnapshots(input: {
  readonly providers: ReadonlyArray<ServerProvider>;
  readonly access: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
}): ReadonlyArray<ServerProvider> {
  return input.providers.filter(
    (provider) =>
      resolveLockedProviderAvailability({
        providerKind: provider.provider,
        snapshot: provider,
        access: input.access,
        credentialMode: input.credentialMode,
        // Every entry carries its own snapshot, so this flag cannot matter:
        // the absent-snapshot case is unreachable by construction.
        hasLiveInventory: true,
      }).state === "usable",
  );
}

export function resolveEligibleSelectableProvider(
  providers: ReadonlyArray<ServerProvider>,
  provider: ProviderKind | null | undefined,
  access: ForgeProviderAccessState,
  credentialMode: ProviderCredentialMode,
): ProviderKind | null {
  // Selection semantics, so the Pick verdict decides: what may be offered is
  // not the same question as what may run.
  const eligibleProviders = resolvePickableProviderSnapshots({ providers, access, credentialMode });
  if (provider && eligibleProviders.some((candidate) => candidate.provider === provider)) {
    return provider;
  }
  // No membership re-check: the resolver only ever names a provider it found
  // selectable IN this list, so the old guard could not fire and its `null`
  // branch was reachable only through the Codex fallback that is now gone.
  return resolveDefaultSelectableProvider(eligibleProviders);
}

/**
 * Resolves a launch provider without silently migrating an explicit selection.
 * A missing preference may use the normal eligible default; a present
 * preference either remains the exact provider or waits/fails closed.
 *
 * The explicit preference is judged by the join's Run verdict, the same
 * evaluation the send path obeys: a launch is an execution, so a provider
 * whose Run verdict is withheld (not on this Environment, delivery pending,
 * the Environment not running) refuses here with that reason and the
 * caller's wait/refusal copy renders it. See
 * docs/adr/pick-and-run-are-projections-of-one-availability-evaluation.md.
 */
export function resolveEligibleLaunchProvider(
  providers: ReadonlyArray<ServerProvider>,
  provider: ProviderKind | null | undefined,
  access: ForgeProviderAccessState,
  credentialMode: ProviderCredentialMode,
): ProviderKind | null {
  if (provider === null || provider === undefined) {
    return resolveEligibleSelectableProvider(providers, null, access, credentialMode);
  }
  const runnableProviders = resolveRunnableProviderSnapshots({
    providers,
    access,
    credentialMode,
    // A launch reads a live, published inventory, never CMMD's last-known
    // catalog (that path is `/new`'s own resolver), so this preserves the
    // Run verdict's prior always-live behavior for this caller.
    hasLiveInventory: true,
  });
  return runnableProviders.some((candidate) => candidate.provider === provider) ? provider : null;
}

export function isSelectableProvider(
  providers: ReadonlyArray<ServerProvider>,
  provider: ProviderKind,
): boolean {
  const snapshot = providers.find((p) => p.provider === provider);
  return snapshot ? isSelectableProviderSnapshot(snapshot) : true;
}

export function getProviderModelCapabilities(
  models: ReadonlyArray<ServerProviderModel>,
  model: string | null | undefined,
  provider: ProviderKind,
): ModelCapabilities {
  const slug = normalizeModelSlug(model, provider);
  return models.find((candidate) => candidate.slug === slug)?.capabilities ?? EMPTY_CAPABILITIES;
}

export function getDefaultServerModel(
  providers: ReadonlyArray<ServerProvider>,
  provider: ProviderKind,
  preferredModel?: string | null,
): string {
  const models = getProviderModels(providers, provider).filter((model) => !model.isLegacy);
  return (
    models.find((model) => model.slug === preferredModel)?.slug ??
    models.find((model) => model.isDefault)?.slug ??
    models.find((model) => model.slug === DEFAULT_MODEL_BY_PROVIDER[provider])?.slug ??
    models.find((model) => !model.isCustom)?.slug ??
    models[0]?.slug ??
    DEFAULT_MODEL_BY_PROVIDER[provider]
  );
}
